import React from 'react';
import Drawer, { FooterSpacer } from '../Drawer';
import Button from '../Button';
import Field from '../Field';
import Tabs from '../Tabs';
import MessageBodyEditor from '../students/MessageBodyEditor';
import { composeLimits } from '../../domain/notifications';
import {
  invoiceEmailFill, invoiceEmailPlaceholders, invoiceEmailTemplates, invoiceEmailWord,
} from '../../domain/invoiceEmail';
import s from '../students/drawers.module.css';

// MMS's Send Invoice drawer (#622), behind an invoice row's Email: From, one
// To — a select of the family's contacts, opening on the invoice recipient —
// "BCC me on this email", the Invoice and Invoice Reminder tabs, Subject and
// Message, each tab loading its own template already filled for the contact on
// To. No attach control: MMS's words say the invoice "is ready and attached to
// this email", and ours is — the sender attaches the stored PDF
// (functions/delivery/invoicePdf.js) — so the drawer says so in one line.
//
// The message is MMS's editor as New Message keeps it (MessageBodyEditor.jsx):
// Markdown in a plain box, the toolbar, and a placeholder menu — here the
// invoice's tokens (`invoiceEmailPlaceholders`), and a Preview that fills them
// for the contact on To rather than naming them, since this drawer has one
// recipient to fill them for. MMS's "You can edit the invoice email template
// in Business Settings" hint is not offered: ours has no editable system
// templates (#608 is custom templates only). The state is useSendInvoice.js.
export default function SendInvoiceDrawer({ sender }) {
  const limits = composeLimits();
  const templates = invoiceEmailTemplates();
  const placeholders = invoiceEmailPlaceholders();
  const tab = Math.max(0, templates.findIndex((t) => t.key === sender.template));
  const nobody = sender.isOpen && !sender.choices.length;

  return (
    <Drawer
      open={sender.isOpen}
      onClose={sender.close}
      title={invoiceEmailWord('title')}
      width={560}
      footer={(
        <>
          <Button variant="secondary" onClick={sender.close}>{invoiceEmailWord('cancel')}</Button>
          <FooterSpacer />
          <Button loading={sender.sending} disabled={nobody} onClick={sender.submit}>{invoiceEmailWord('send')}</Button>
        </>
      )}
    >
      {sender.fromChoices?.length ? (
        <Field label={invoiceEmailWord('from')}>
          <select
            className={s.input}
            aria-label={invoiceEmailWord('from')}
            value={sender.from || ''}
            onChange={(e) => sender.change({ from: e.target.value })}
          >
            {sender.fromChoices.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
        </Field>
      ) : null}
      <div className={s.section}>
        <Field label={invoiceEmailWord('to')} error={nobody ? invoiceEmailWord('invoice_email_no_recipient') : null}>
          <select
            className={s.input}
            aria-label={invoiceEmailWord('to')}
            value={sender.to ?? ''}
            disabled={nobody}
            onChange={(e) => sender.pickTo(Number(e.target.value))}
          >
            {sender.choices.map((c, i) => (
              <option key={c.recipient.address} value={i}>
                {c.recipient.name ? `${c.recipient.name} <${c.recipient.address}>` : c.recipient.address}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {sender.canBccMe ? (
        <label className={`${s.section} ${s.check}`}>
          <input
            type="checkbox"
            checked={sender.bccMe}
            onChange={(e) => sender.change({ bccMe: e.target.checked })}
          />
          {invoiceEmailWord('bccMe')}
        </label>
      ) : null}
      <div className={s.section}>
        <Tabs
          tabs={templates.map((t) => t.label)}
          current={tab}
          onChange={(i) => sender.pickTemplate(templates[i].key)}
        />
      </div>
      <div className={s.section}>
        <Field label={invoiceEmailWord('subject')} required>
          <input
            className={s.input}
            value={sender.subject}
            maxLength={limits.subject}
            onChange={(e) => sender.change({ subject: e.target.value })}
          />
        </Field>
      </div>
      <div className={s.section}>
        <MessageBodyEditor
          label={invoiceEmailWord('message')}
          required
          value={sender.body}
          onChange={(body) => sender.change({ body })}
          edits={sender}
          error={sender.error}
          notice={sender.notice}
          placeholders={placeholders}
          previewText={(text) => (sender.facts ? invoiceEmailFill(text, sender.facts) : text)}
        />
      </div>
      <div className={`${s.section} ${s.hint}`}>{invoiceEmailWord('attached')}</div>
    </Drawer>
  );
}
